import { useEffect, type Dispatch } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { m } from "framer-motion";
import { Minus, Plus, ShoppingCart, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Slider,
  SliderControl,
  SliderIndicator,
  SliderThumb,
  SliderTrack,
} from "@/components/ui/slider";
import {
  hasPerSeatTiers,
  MAX_CLINIC_DOCTORS,
  MAX_NON_SEAT_QUANTITY,
  seatTiersOf,
} from "@/components/pricing/clinic-seat-pricing";
import type { PlanPriceResult } from "@/lib/pricing/calculate-plan-price";
import { packLabel } from "@/lib/pricing/pack-labels";

import type { CheckoutAction, CheckoutState, PlanAddonOption, PricingTier } from "./checkout-types";

// Legacy source: src/components/billing/checkout/checkout-step-configure.tsx
// (1:1 port). next-intl → Lingui macros (interpolated legacy keys become the
// small *Msg helper components below so msgids keep the named placeholders),
// motion → m, PricingTier imported from ./checkout-types instead of
// @/components/pricing/pricing-section.

// ---------------------------------------------------------------------------
// Interpolated messages — msgid must match legacy en.json verbatim, so the
// placeholders are bound to simple identifiers via these helpers.
// ---------------------------------------------------------------------------

function ActiveTierDiscountMsg({ percent, price }: { percent: number; price: string }) {
  // Legacy key: checkout.activeTierDiscount
  return (
    // prettier-ignore
    <Trans>{percent}% off — {price}/doctor</Trans>
  );
}

function NextTierNudgeMsg({ count, percent }: { count: number; percent: number }) {
  // Legacy key: checkout.nextTierNudge
  return (
    // prettier-ignore
    <Trans>Add {count} more to unlock {percent}% discount!</Trans>
  );
}

function SavingsVsMonthlyMsg({ amount }: { amount: string }) {
  // Legacy key: checkout.savingsVsMonthly
  return (
    // prettier-ignore
    <Trans>save {amount}</Trans>
  );
}

function VolumeSavingsMsg({ amount, percent }: { amount: string; percent: number }) {
  // Legacy key: checkout.volumeSavingsMsg
  return (
    // prettier-ignore
    <Trans>You save {amount}/month ({percent}% volume discount)</Trans>
  );
}

// D2 (`_docs/decizii-abonare-founding-2026-09-19.md` §2.5) — clinic plans are
// "base + flat rate per covered doctor" (rate from the bracket the head count
// falls into). New msgids, so placeholders stay bound to plain identifiers.
function SeatsLineMsg({ count, price }: { count: number; price: string }) {
  return (
    // prettier-ignore
    <Trans>{count} covered doctors × {price}</Trans>
  );
}

function SeatBracketMsg({ min, max }: { min: number; max: number }) {
  return (
    // prettier-ignore
    <Trans>{min}–{max} doctors</Trans>
  );
}

function SeatBracketOpenMsg({ min }: { min: number }) {
  return <Trans>{min}+ doctors</Trans>;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CheckoutStepConfigureProps {
  state: CheckoutState;
  price: PlanPriceResult | null;
  dispatch: Dispatch<CheckoutAction>;
  onContinue: () => void;
  onBack?: () => void;
  planData: PricingTier;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CheckoutStepConfigure({
  state,
  price,
  dispatch,
  onContinue,
  onBack,
  planData,
}: CheckoutStepConfigureProps) {
  const { t, i18n } = useLingui();

  const { interval, quantity, addons, availablePacks } = state;

  // D2: a clinic PLAN is billed monthly, per covered doctor. The gate is the
  // plan's own `perSeatTiers` — NOT the entity type: a clinic may also buy
  // ordinary `provider_*` plans to cover its doctors (assertPlanFitsEntity
  // allows it) and those keep the graduated ladder and the annual option.
  // `basePriceMonthly` / `perSeatTiers` come over the wire on facility plans but
  // are not declared on this app's `PricingTier` (checkout-types) yet; the
  // helpers declare them on `SeatPricedPlan`, so every read is typed.
  const seatPriced = hasPerSeatTiers(planData);
  const seatBrackets = seatPriced ? (seatTiersOf(planData) ?? []) : [];

  const hasQuantity = !!(planData.volumeTiers?.length || planData.extraProviderPrice || seatPriced);
  const hasPacks = availablePacks.length > 0;

  // Clinic plans are monthly-only (decision 2): the toggle is hidden, and an
  // `initialInterval: "annual"` handed in by the caller is corrected here so the
  // checkout can never post `interval: annual` for a seat-priced plan.
  useEffect(() => {
    if (seatPriced && interval !== "monthly") {
      dispatch({ type: "SET_INTERVAL", interval: "monthly" });
    }
  }, [seatPriced, interval, dispatch]);

  const labelOf = (opt: PlanAddonOption) => packLabel(i18n, opt);

  const sym = "€";
  const fmt = (n: number) => `${sym}${Math.round(n * 100) / 100}`;

  // Seat-priced clinic plans go up to the server's own cap; the graduated
  // provider ladder keeps its historic 100.
  const maxQuantity = seatPriced ? MAX_CLINIC_DOCTORS : MAX_NON_SEAT_QUANTITY;

  return (
    <m.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
      className="space-y-5 p-5"
    >
      {/* Interval toggle — hidden for seat-priced clinic plans (monthly only,
          decision 2). A facility buying provider_* plans still sees it. */}
      <div
        className={`bg-muted/60 relative inline-flex w-full rounded-2xl p-1 ${
          seatPriced ? "hidden" : ""
        }`}
      >
        <button
          onClick={() => dispatch({ type: "SET_INTERVAL", interval: "monthly" })}
          className={`relative z-10 flex-1 rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors ${
            interval === "monthly" ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          <Trans>Monthly</Trans>
          {interval === "monthly" && (
            <m.div
              layoutId="cfg-interval-pill"
              className="bg-card absolute inset-0 rounded-xl shadow-md"
              style={{ zIndex: -1 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            />
          )}
        </button>
        <button
          onClick={() => dispatch({ type: "SET_INTERVAL", interval: "annual" })}
          className={`relative z-10 flex-1 rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors ${
            interval === "annual" ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          <span className="flex items-center justify-center gap-1.5">
            <Trans>Annual</Trans>
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[0.625rem] font-semibold text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
              <Trans>2 months free</Trans>
            </span>
          </span>
          {interval === "annual" && (
            <m.div
              layoutId="cfg-interval-pill"
              className="bg-card absolute inset-0 rounded-xl shadow-md"
              style={{ zIndex: -1 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            />
          )}
        </button>
      </div>

      {/* Quantity selector — only for plans with volume/extra pricing */}
      {hasQuantity && (
        <div className="bg-muted/30 rounded-xl border p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold">
              {seatPriced ? <Trans>Covered doctors</Trans> : <Trans>Number of doctors</Trans>}
            </span>
            <Badge variant="secondary" className="text-xs">
              {seatPriced ? (
                <>
                  {fmt(price?.perProviderPrice ?? 0)}/{t`doctor`}
                </>
              ) : quantity === 1 ? (
                <Trans>Individual</Trans>
              ) : (
                <Trans>Clinic</Trans>
              )}
            </Badge>
          </div>

          {/* Stepper */}
          <div className="mb-3 flex items-center justify-center gap-4">
            <m.button
              whileTap={{ scale: 0.9 }}
              onClick={() => dispatch({ type: "SET_QUANTITY", quantity: quantity - 1 })}
              disabled={quantity <= 1}
              className="bg-muted hover:bg-muted/80 flex h-11 w-11 items-center justify-center rounded-full transition-colors disabled:opacity-30"
            >
              <Minus className="h-5 w-5" />
            </m.button>
            <input
              type="number"
              min={1}
              max={maxQuantity}
              value={quantity}
              onChange={(e) =>
                dispatch({
                  type: "SET_QUANTITY",
                  // Clamp: a typed 500 (or 0) must not escape the stepper's own
                  // bounds — the + button stops at 100 and so does this.
                  quantity: Math.min(maxQuantity, Math.max(1, parseInt(e.target.value) || 1)),
                })
              }
              className="bg-background text-foreground focus:ring-primary/30 w-20 [appearance:textfield] rounded-xl border px-3 py-2 text-center text-2xl font-bold tabular-nums outline-none focus:ring-2 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <m.button
              whileTap={{ scale: 0.9 }}
              onClick={() => dispatch({ type: "SET_QUANTITY", quantity: quantity + 1 })}
              disabled={quantity >= maxQuantity}
              className="bg-muted hover:bg-muted/80 flex h-11 w-11 items-center justify-center rounded-full transition-colors disabled:opacity-30"
            >
              <Plus className="h-5 w-5" />
            </m.button>
          </div>

          {/* Slider */}
          <Slider
            min={1}
            max={50}
            step={1}
            value={quantity}
            onValueChange={(val) => dispatch({ type: "SET_QUANTITY", quantity: val as number })}
          >
            <SliderControl>
              <SliderTrack>
                <SliderIndicator />
              </SliderTrack>
              <SliderThumb />
            </SliderControl>
          </Slider>
          <div className="text-muted-foreground mt-1 flex justify-between text-xs">
            <span>1</span>
            <span>50+</span>
          </div>

          {/* D2 per-doctor brackets — active bracket highlighted */}
          {seatPriced && seatBrackets.length > 0 && (
            <div className="mt-3 space-y-1">
              {seatBrackets.map((b) => (
                <div
                  key={b.minSeats}
                  className={`flex items-center justify-between rounded-lg px-3 py-1.5 text-xs transition-colors ${
                    price?.perProviderPrice === b.pricePerSeat
                      ? "bg-primary/10 text-primary font-semibold"
                      : "text-muted-foreground"
                  }`}
                >
                  <span>
                    {b.maxSeats >= 999 ? (
                      <SeatBracketOpenMsg min={b.minSeats} />
                    ) : (
                      <SeatBracketMsg min={b.minSeats} max={b.maxSeats} />
                    )}
                  </span>
                  <span className="tabular-nums">
                    {fmt(b.pricePerSeat)}/{t`doc`}
                  </span>
                </div>
              ))}
              <p className="text-success pt-1 text-[0.6875rem] font-medium">
                <Trans>Doctors covered by the clinic pay nothing.</Trans>
              </p>
            </div>
          )}

          {/* Volume tier indicator */}
          {!seatPriced && price?.activeTier && price.activeTier.discountPct > 0 && (
            <m.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-primary/5 border-primary/20 mt-3 rounded-lg border p-3"
            >
              <div className="flex items-center gap-2 text-sm">
                <Sparkles className="text-primary h-4 w-4 shrink-0" />
                <span>
                  <ActiveTierDiscountMsg
                    percent={price.activeTier.discountPct}
                    price={fmt(price.perProviderPrice)}
                  />
                </span>
              </div>
              {price.nextTier && price.providersToNextTier <= 5 && (
                <p className="text-muted-foreground mt-1 pl-6 text-xs">
                  <NextTierNudgeMsg
                    count={price.providersToNextTier}
                    percent={price.nextTier.discountPct}
                  />
                </p>
              )}
            </m.div>
          )}

          {/* Volume tiers overview */}
          {!seatPriced && planData.volumeTiers && planData.volumeTiers.length > 0 && (
            <div className="mt-3 space-y-1">
              {planData.volumeTiers.map((tier) => (
                <div
                  key={tier.minProviders}
                  className={`flex items-center justify-between rounded-lg px-3 py-1.5 text-xs transition-colors ${
                    price?.activeTier === tier
                      ? "bg-primary/10 text-primary font-semibold"
                      : "text-muted-foreground"
                  }`}
                >
                  <span>
                    {tier.minProviders}–{tier.maxProviders} {t`doctors`}
                  </span>
                  <span>
                    {fmt(tier.pricePerProvider)}/{t`doc`}
                    {tier.discountPct > 0 && (
                      <span className="ml-1 text-emerald-600">-{tier.discountPct}%</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add-on packs — dynamically sourced from PlanFeature.batchSize/batchPrice
          via /api/billing/plan-addons. Each pack has its own stepper so the
          user can buy multiple (+50, +100, +150 bookings; +100, +200 SMS; etc.). */}
      {hasPacks && (
        <div>
          <div className="mb-2 text-sm font-semibold">
            <Trans>Add-ons</Trans>
          </div>
          <div className="space-y-2">
            {availablePacks.map((opt) => {
              const count = addons.packs[opt.featureKey] ?? 0;
              const unitsAdded = count * opt.batchSize;
              const totalEur = count * opt.batchPriceEur;
              const baseLine =
                opt.basePlanLimit != null && opt.basePlanLimit >= 0
                  ? `${opt.basePlanLimit} → ${opt.basePlanLimit + unitsAdded}`
                  : null;
              return (
                <div
                  key={opt.featureKey}
                  className={`rounded-xl border p-3 transition-all ${
                    count > 0
                      ? "border-primary/30 bg-primary/5 ring-primary/20 ring-2"
                      : "border-border"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-foreground text-sm font-semibold">{labelOf(opt)}</div>
                      <div className="text-muted-foreground mt-0.5 text-xs">
                        +{opt.batchSize} / {fmt(opt.batchPriceEur)}
                        {count > 0 && (
                          <span className="text-foreground ml-2 font-medium tabular-nums">
                            × {count} = {fmt(totalEur)}
                          </span>
                        )}
                      </div>
                      {baseLine && count > 0 && (
                        <div className="text-muted-foreground/70 mt-0.5 text-[0.625rem] tabular-nums">
                          {baseLine}
                        </div>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <m.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() =>
                          dispatch({
                            type: "SET_ADDON_PACK",
                            featureKey: opt.featureKey,
                            count: count - 1,
                          })
                        }
                        disabled={count <= 0}
                        className="bg-muted hover:bg-muted/80 flex h-9 w-9 items-center justify-center rounded-full transition-colors disabled:opacity-30"
                        aria-label={t`Decrease`}
                      >
                        <Minus className="h-4 w-4" />
                      </m.button>
                      <span className="w-6 text-center text-sm font-bold tabular-nums">
                        {count}
                      </span>
                      <m.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() =>
                          dispatch({
                            type: "SET_ADDON_PACK",
                            featureKey: opt.featureKey,
                            count: count + 1,
                          })
                        }
                        disabled={count >= 20}
                        className="bg-muted hover:bg-muted/80 flex h-9 w-9 items-center justify-center rounded-full transition-colors disabled:opacity-30"
                        aria-label={t`Increase`}
                      >
                        <Plus className="h-4 w-4" />
                      </m.button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Price summary (simplified — sidebar has full breakdown) */}
      {price && (
        <div className="bg-muted/30 space-y-2 rounded-xl border p-4">
          <div className="mb-2 text-sm font-semibold">
            <Trans>Price summary</Trans>
          </div>

          {/* Base plan — D2 clinic plans show the base subscription on its own
              line, then one line for every covered doctor at the bracket rate. */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {state.selectedPlanName}
              {!seatPriced && planData.providersIncluded && planData.providersIncluded > 0
                ? ` (${planData.providersIncluded} ${t`included`})`
                : ""}
            </span>
            <span className="font-medium tabular-nums">{fmt(price.basePlanPrice)}</span>
          </div>

          {seatPriced && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                <SeatsLineMsg
                  count={price.extraProvidersCount}
                  price={fmt(price.perProviderPrice)}
                />
              </span>
              <span className="font-medium tabular-nums">{fmt(price.extraProvidersTotal)}</span>
            </div>
          )}

          {/* Extra providers */}
          {!seatPriced && price.extraProvidersCount > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                + {price.extraProvidersCount} {t`extra doctors`} × {fmt(price.perProviderPrice)}
              </span>
              <span className="font-medium tabular-nums">{fmt(price.extraProvidersTotal)}</span>
            </div>
          )}

          {/* Pack add-ons — one line per selected pack (quantity × batch price) */}
          {availablePacks
            .filter((opt) => (addons.packs[opt.featureKey] ?? 0) > 0)
            .map((opt) => {
              const count = addons.packs[opt.featureKey] ?? 0;
              return (
                <div key={opt.featureKey} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    + {count} × {labelOf(opt)}
                  </span>
                  <span className="font-medium tabular-nums">{fmt(count * opt.batchPriceEur)}</span>
                </div>
              );
            })}

          {/* Total line */}
          <div className="border-border/50 border-t pt-2">
            <div className="flex items-center justify-between">
              <span className="text-foreground text-sm font-bold">
                {interval === "annual" && !seatPriced
                  ? `${t`Total`} / ${t`Annual`.toLowerCase()}`
                  : `${t`Total`} / ${t`Monthly`.toLowerCase()}`}
              </span>
              <span className="text-foreground text-lg font-bold tabular-nums">
                {fmt(
                  interval === "annual" && !seatPriced ? price.intervalTotal : price.monthlyTotal,
                )}
              </span>
            </div>

            {/* Annual equivalent monthly + savings */}
            {interval === "annual" && !seatPriced && (
              <div className="text-muted-foreground mt-1 flex items-center justify-between text-xs">
                <span>
                  ≈ {fmt(Math.round(price.intervalTotal / 12))}/{t`Monthly`.toLowerCase()}
                </span>
                <span className="text-emerald-600 dark:text-emerald-400">
                  <SavingsVsMonthlyMsg
                    amount={fmt(price.monthlyTotal * 12 - price.intervalTotal)}
                  />
                </span>
              </div>
            )}
          </div>

          {/* Volume savings */}
          {!seatPriced && price.volumeSavings > 0 && (
            <div className="flex items-center gap-2 rounded-lg bg-emerald-50 p-2.5 dark:bg-emerald-950/30">
              <Sparkles className="h-4 w-4 shrink-0 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                <VolumeSavingsMsg
                  amount={fmt(price.volumeSavings)}
                  percent={price.volumeDiscountPct}
                />
              </span>
            </div>
          )}
        </div>
      )}

      {/* Footer navigation — hidden on mobile (orchestrator handles mobile footer) */}
      <div className="hidden gap-3 pt-2 md:flex">
        {onBack && (
          <Button variant="outline" size="lg" onClick={onBack} className="shrink-0">
            <Trans>Back</Trans>
          </Button>
        )}
        <Button className="flex-1 gap-2" size="lg" onClick={onContinue}>
          <ShoppingCart className="h-4 w-4" />
          <Trans>Continue</Trans>
        </Button>
      </div>
    </m.div>
  );
}
